// Date: 15 Jan, 2025
// Ref: Meeting for ask cn Versions control.
// Changes: Added VersionSelect file for verson switching.

import { useCallback, useContext, useEffect, useState } from "react";
import Select, { components } from "react-select";
import { debounce } from "lodash";
import AsyncSelect from "react-select/async";
import { useLocation } from "react-router-dom";
import { UserContext } from "../UserContext";
import { BaseURL } from "../BaseURL";
import toast from "react-hot-toast";

export const VersionSelect = ({ selectedOption, setSelectedOption }) => {
  const {
    darkTheme,
    tokenMode,
    product,
    setIsVersionNameClicked,
    isVersionNameClicked,
    // VERSION CHANGE LOADER
    setCheckArticleLoader,
  } = useContext(UserContext);
  const location = useLocation();
  // Date: 12 Aug 2026
  // Ref: "Article not found" false-negative on version switch bug report.
  // Changes: Read the article slug straight off the current URL instead of
  // context's `selectedHighlight` -- the same "strip the leading version
  // segment" pattern Home.js's own checkingArticleDataFunc uses for OD links,
  // which is proven reliable, whereas selectedHighlight isn't guaranteed to
  // still hold this article's slug at click time (e.g. it's briefly nulled
  // by other sidebar handlers -- see subToggleDropdown/toggleDropdown).
  const versionRegex = /\b\d{1,3}([.-]\d{1,3}){1,3}\b/;
  // Date: 12 Aug 2026
  // Ref: Same route-prefix problem MainWrapper.js's own endPath computation
  // already solves (see its "8 Aug 2026" comment) -- on a URL like
  // "/cloudframe/codenavigator/2.0.2/testing2", stripping the version leaves
  // a double slash ("codenavigator//testing2") that isn't at the very start
  // or end of the string, so the old ^\/|\/$ trim never removed it and the
  // launch prefix stayed attached, meaning it could never match a real post
  // slug -- every version switch reported "not found" even when the target
  // version had the exact same article. Mirror MainWrapper.js's collapse +
  // prefix-strip here instead of duplicating the naive version.
  const productLaunchPrefixes = [
    "cloudframe/codenavigator",
    "cloudframe/continuum",
    "cloudframe",
  ].sort((a, b) => b.length - a.length);
  const getCurrentArticleSlug = () => {
    const rawPath = location.pathname
      .replace(versionRegex, "")
      .replace(/\/{2,}/g, "/")
      .replace(/^\/|\/$/g, "");
    const matchedPrefix = productLaunchPrefixes.find(
      (prefix) => rawPath === prefix || rawPath.startsWith(`${prefix}/`),
    );
    return matchedPrefix
      ? rawPath.slice(matchedPrefix.length).replace(/^\/+/, "")
      : rawPath;
  };
  // The personal/versions endpoint defaults to "code-navigator" when no
  // product slug is sent, so a Continuum user with no product param would
  // always get Code Navigator's version list back.
  const productSlug = product === "continuum" ? "continuum" : "code-navigator";
  // ACCESS TOKEN
  const accessToken = localStorage.getItem("access_token");
  // CN ACCESS TOKEN
  const cnAccessToken = localStorage.getItem("cn_access_token");

  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Date: 1 March 2025
  // Ref: "As per Manigandan, to show pop-up box if article not available on perticular version."
  // Changes: New code added.
  const checkingArticleDataFunc = async (vData) => {
    setCheckArticleLoader(true);
    const versionData = {
      version: vData.slug ? vData.slug : "",
      // Date: 25 Sep 2026
      // Ref: "Not found" error while switching version in Continuum.
      // Changes: Scope the lookup to the current KB -- without it the
      // backend defaults to "code-navigator" (see PostController@posts),
      // can't find the Continuum version slug and returns 404.
      product: productSlug,
    };
    const queryParams = new URLSearchParams(versionData).toString();
    const versionValidate = queryParams ? queryParams : "";

    try {
      const res = await fetch(
        `${BaseURL}/${tokenMode}/posts?${versionValidate}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${
              tokenMode === "personal" ? accessToken : cnAccessToken
            }`,
          },
        },
      );
      const data = await res.json();

      if (res.ok && data?.success) {
        const myAllPosts = await data.data.flatMap(
          (category) => category.posts,
        );

        // CHECKING ARTICLE
        const currentArticleSlug = getCurrentArticleSlug();
        const filtered = await myAllPosts.filter(
          (post) => post.slug === currentArticleSlug,
        );
        if (filtered.length > 0) {
          localStorage.setItem("versionNameId", vData.slug);
          await setIsVersionNameClicked(true);
          setSelectedOption(vData);
        } else {
          // Date: 12 Aug 2026
          // Ref: "Don't popup Article Not Found modal while switching version,
          // always allow switching and land on the first article of the
          // selected version" bug report.
          // Changes: Skip the blocking modal and land directly on the
          // selected version's first article, same destination the modal's
          // "Continue to Version" button used to send users to.
          // Date: 25 Sep 2026
          // Changes: Keep the launch prefix (e.g. "cloudframe/continuum") --
          // a bare "/<version>" hits the product's catch-all route, which
          // redirects to the prefix root and drops the selected version.
          const currentPath = location.pathname.replace(/^\/+/, "");
          const launchPrefix = productLaunchPrefixes.find(
            (prefix) =>
              currentPath === prefix || currentPath.startsWith(`${prefix}/`),
          );
          window.location.href = `${window.location.origin}/${
            launchPrefix ? `${launchPrefix}/` : ""
          }${vData.slug}`;
        }
      } else {
        toast.error(`Error: ${res.status} ${res.statusText}`);
      }
    } catch (err) {
      if (err.message === "Failed to fetch") {
        toast.error("Failed to fetch");
      } else {
        toast.error(err.message);
      }
    } finally {
      setCheckArticleLoader(false);
    }
  };
  const stopCheckLinkClickFunc = (sVersion) => {
    toast("Please wait, it's processing..");
    checkingArticleDataFunc(sVersion);
  };

  // useEffect(() => {
  //   const fetchInitialData = async () => {
  //     if (selectedOption !== "") {
  //       setIsLoading(true);
  //       const response = await fetch(
  //         `${BaseURL}/versions?q=${selectedOption}`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Accept: "application/json",
  //             Authorization: `Bearer ${
  //               tokenMode === "personal" ? accessToken : cnAccessToken
  //             }`,
  //           },
  //         }
  //       );
  //       const data = await response.json();
  //       const myFilter = data.data.filter(
  //         (item) => item.id === parseInt(selectedOption)
  //       );
  //       const transformedOptions = myFilter.map((item) => ({
  //         value: item.id,
  //         label: item.name,
  //       }));
  //       const myFind = transformedOptions.find(
  //         (item) => item.value === parseInt(selectedOption)
  //       );
  //       setSelectedOption(myFind);
  //       if (data.status === false || data.status === true) {
  //         setIsLoading(false);
  //       }
  //     }
  //   };
  //   fetchInitialData();
  // }, []);
  useEffect(() => {
    const fetchInitialData = async () => {
      if (selectedOption !== "") {
        setIsLoading(true);
        const myFind = options.find(
          // (item) => item.value === parseInt(selectedOption)
          (item) => item.slug === selectedOption,
        );
        if (myFind) {
          setSelectedOption(myFind);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    };
    fetchInitialData();
    // Date: 25 Sep 2026
    // Ref: Header version dropdown showing "Select" instead of the version.
    // Changes: Also re-sync when selectedOption changes -- MainWrapper.js
    // resets versionName to a plain slug string after every posts fetch
    // (including the one right after a version switch), and with only
    // [options] as a dependency that string was never mapped back to an
    // option object, so react-select fell back to the placeholder.
  }, [options, selectedOption]);

  // react-select can't render a plain slug string, so resolve it against the
  // loaded options for display regardless of which shape context holds.
  const displayValue =
    typeof selectedOption === "string"
      ? options.find((item) => item.slug === selectedOption) || null
      : selectedOption;

  // Function to load options based on user input
  const loadOptions = (inputValue, callback) => {
    const res = fetch(
      `${BaseURL}/${tokenMode}/versions?q=${inputValue}&product=${productSlug}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${
            tokenMode === "personal" ? accessToken : cnAccessToken
          }`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        const options = data.data.map((item) => ({
          label: `Version- ${item.name}`,
          value: item.id,
          slug: item.slug,
        }));
        callback(options);
        setOptions(options);
        // setIsLoading(false)
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        callback([]);
        // setIsLoading(false)
      });
  };
  const debouncedLoadOptions = debounce(loadOptions, 400);

  // Handle option select
  const handleChange = async (option) => {
    // setSelectedOption(option);
    // await setIsVersionNameClicked(false);

    // if (option?.value) {
    //   localStorage.setItem("versionNameId", option.slug);
    //   await setIsVersionNameClicked(true);
    // }
    if (option?.value) {
      // Date: 1 March 2025
      stopCheckLinkClickFunc(option);
    }
  };

  // Custom styling
  const customStyles = {
    container: (provided) => ({
      ...provided,
      width: "100%",
      margin: "0 auto",
      color: "white",
    }),
    control: (provided) => ({
      ...provided,
      // Date: 8 Aug 2026 -- was an exact match for the header's own blue, so the
      // pill had no visible edge and read as plain text rather than a control.
      backgroundColor: "rgba(255,255,255,0.08)",
      boxShadow: "none",
      "&:hover": {
        borderColor: "rgba(255,255,255,0.35)",
      },
      // borderRadius: "12px",
      borderRadius: "9999px",
      border: "1px solid rgba(255,255,255,0.2)",
      // padding: "0px 2px",
      // textTransform: "uppercase",
      // border: "2px solid #aaa9",
      fontSize: "14px",
      height: "35px",
      minHeight: "35px",
      padding: "0px",
      cursor: "pointer",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      // backgroundColor: `${darkTheme ? "#52565e" : "#F6F8FC"}`,
      // textTransform: "uppercase",
      // border: "1px solid #E5E5E5",
      padding: "0 6px",
      width: "166px",
      zIndex: 9999,
    }),
    // menuList: (provided) => ({
    //   ...provided,
    //   backgroundColor: `${darkTheme ? "#52565e" : "#F6F8FC"}`, // Ensure the list inside matches the menu background
    //   // padding: 0,
    // }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0a61dc"
        : state.isFocused
          ? "#fff"
          : "#fff",
      color: state.isSelected ? "white" : "black",
      "&:active": {
        backgroundColor: "#aaa",
        color: "white",
      },
      padding: "4px 10px",
      borderRadius: "8px",
      cursor: "pointer",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "white",
      // backgroundColor: `${darkTheme ? "#52565e" : "#F6F8FC"}`,
      // padding: "2px 6px",
      // borderRadius: "4px"
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "white",
      fontWeight: "400",
      fontSize: "14px",
      // textTransform: "uppercase",
    }),
    input: (provided) => ({
      ...provided,
      color: "white",
      margin: "0px",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? "#fff" : "#bbb",
      "&:hover": {
        color: "#fff", // Change color on hover
      },
      // paddingRight: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "#999",
      display: "none",
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingLeft: "10px",
    }),
  };

  return (
    <AsyncSelect
      styles={customStyles}
      cacheOptions
      loadOptions={debouncedLoadOptions}
      defaultOptions
      onChange={handleChange}
      value={displayValue}
      // defaultValue={defValue}
      placeholder="Select"
      isLoading={isLoading}
      // components={{
      //   MenuList: (props) => <MenuList {...props} isLoading={isLoading} />,
      // }}
      closeMenuOnSelect={true}
      className="react-select-container"
      classNamePrefix="react-select"
      // isClearable
      isDisabled={isLoading}
      isSearchable={false}
    />
  );
};
